import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';

import { chartTimePeriod } from '../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
import stationManagementApi from '../../../utils/api/station-management';

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

interface ChartWidthInterface {
  type: 'percentage' | 'pixel';
  width: number;
}

/**
 * Custom square bar component
 */
function TriangleBar(properties: any) {
  const { fill, x, y, width, height } = properties;

  const blockHeight = 5;
  const blockGap = 2;
  const blockCount = Math.floor(height / (blockHeight + blockGap));

  return (
    <>
      {Array.from({ length: blockCount }).map((_, index) => (
        <rect
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          x={x}
          y={y + height - (index + 1) * (blockHeight + blockGap)}
          width={width}
          height={blockHeight}
          fill={fill}
          onClick={() => {}}
          style={{ cursor: 'pointer' }}
        />
      ))}
    </>
  );
}
/**
 * Uptime BY Station Chart
 * @returns {any} The UptimeStationChart component
 */
function UptimeStationChart() {
  const { t } = useTranslation('stationManagement');
  const dispatch = useAppDispatch();
  const { selectedStationPerformanceList } = useAppSelector((state) => state.stationManagement);
  const [loading, setLoading] = useState<boolean>(false);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [chartWidth, setChartWidth] = useState<ChartWidthInterface>({
    type: 'percentage',
    width: 100,
  });
  /**
   * Function to show custom tooltip
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="energy-report-tooltip">
          <div className="tooltip-values">
            <p style={{ color: '#4ABDFB' }}>
              {t('stationName')}: {data.stationName}
            </p>
            <p style={{ color: '#2E9E3A' }}>
              {t('totalOnlineHours')}: {data.rawHours}
            </p>
          </div>
        </div>
      );
    }
    return null;
  }

  /**
   * Fetch power consumed data based on time range
   * @param range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    setLoading(true);
    try {
      const response = await stationManagementApi.fetchUptimeChartData({
        timePeriod: range,
        chargingStation: selectedStationPerformanceList?.id,
      });
      if (response.status === 200) {
        const values = response.data.map((item: any) => item.totalOnlineHours);
        const maxValue = Math.max(...values) || 1;

        const formatted = response.data
          .map((item: any) => ({
            stationName: item.stationName,
            rawHours: item.totalOnlineHours,
            totalOnlineHours: (item.totalOnlineHours / maxValue) * 100,
          }))
          // 🔽 DESCENDING ORDER (greater → smaller)
          .sort((a: any, b: any) => b.rawHours - a.rawHours);

        setBarGraphData(formatted);
      }

      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  useEffect(() => {
    if (selectedStationPerformanceList?.id) {
      fetchPowerConsumedReport('last24hours');
    }
  }, [selectedStationPerformanceList]);

  useEffect(() => {
    if (barGraphData.length <= 10) {
      setChartWidth({
        type: 'percentage',
        width: 100,
      });
    } else {
      setChartWidth({
        type: 'pixel',
        width: barGraphData.length * 50,
      });
    }
  }, [barGraphData]);

  return (
    <div className="uptime-by-station-chart-wrap">
      <div className="uptime-by-station-container">
        <div className="uptime-by-header">
          <div className="uptime-title">
            <p>{t('uptimePercentByStation')}</p>
          </div>
          <CustomToggleButton
            labels={chartTimePeriod.map((duration: { label: any }) => t(duration.label))}
            onToggle={(index, label) =>
              fetchPowerConsumedReport(
                chartTimePeriod.find((duration) => duration.label === label)?.value ||
                  'last24hours',
              )
            }
            defaultActiveIndex={0}
            type="chart"
          />
        </div>
        {loading ? (
          <div className="loader">
            <BatteryLoader />
          </div>
        ) : (
          <div className="uptime-by-station-chart-container">
            <div className="uptime-by-station-chart">
              <ResponsiveContainer
                width={chartWidth.type === 'pixel' ? chartWidth.width : `${chartWidth.width}%`}
                height={229}>
                <BarChart data={barGraphData}>
                  <XAxis
                    dataKey="stationName"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12, fill: '#666', fontWeight: 500 }}
                    interval={0}
                    height={20}
                    tickFormatter={(value) =>
                      value?.length > 3 ? `${value.slice(0, 3)}...` : value
                    }
                  />
                  <YAxis
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#666', fontWeight: 600 }}
                    width={35}
                    padding={{ top: 20, bottom: 10 }}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                  <Bar
                    dataKey="totalOnlineHours"
                    fill="#4ABDFB"
                    shape={<TriangleBar />}
                    barSize={30}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UptimeStationChart;
